import React, { PropTypes } from "react";
import { connect } from "react-redux";
import { DataTable } from "../../../../common/DataTable";
import { FormattedMessage, FormattedDate } from "react-intl";
import { DownloadS3Button } from "../../../../common";
import { ACCOUNTING_EXPORTS } from "../../../../../utils/data/models";
import { bindActionCreators } from "redux";
import * as dataActions from "../../../../../utils/data/dataActions";
import { getS3Informations } from "../../../../common/environmentProperties";
import { toast } from "react-toastify";

const S3_BUCKET = getS3Informations();

const ROWS_PER_PAGE = 12;

class AccountingExportsDataTable extends React.Component {
  constructor(props, context) {
    super(props, context);

    this.getData = this.getData.bind(this);
    this.regroupReports = this.regroupReports.bind(this);
    this.sortPeriodsDesc = this.sortPeriodsDesc.bind(this);

    this.goFirstPage = this.goFirstPage.bind(this);
    this.goPrevPage = this.goPrevPage.bind(this);
    this.goNextPage = this.goNextPage.bind(this);
    this.goLastPage = this.goLastPage.bind(this);

    this.state = {
      sortedData: [],
      currentPage: 0,
    };
  }

  componentDidUpdate(prevProps) {
    // Rebuild + sort when items change
    if (this.props.items.length > 0 && prevProps.items !== this.props.items) {
      try {
        this.regroupReports();
      } catch (error) {
        toast(<FormattedMessage id="genericDataFetchError" />, {
          type: toast.TYPE.ERROR,
          autoClose: true,
        });
      }

      return;
    }

    if (
      this.props.items.length === 0 &&
      this.state.sortedData &&
      this.state.sortedData.length > 0
    ) {
      this.setState({ sortedData: [], currentPage: 0 });
      return;
    }
  }

  getData() {
    return this.getPagedData();
  }

  toSortKey(period) {
    if (!period) return "";
    return period.replace("/", "-");
  }

  getPagedData() {
    const { sortedData, currentPage } = this.state;
    if (!sortedData || sortedData.length === 0) return [];

    const start = currentPage * ROWS_PER_PAGE;
    const end = start + ROWS_PER_PAGE;
    return sortedData.slice(start, end);
  }

  getTotalPages() {
    const total = this.state.sortedData ? this.state.sortedData.length : 0;
    if (total === 0) return 1;
    return Math.max(1, Math.ceil(total / ROWS_PER_PAGE));
  }

  clampPage(page) {
    const totalPages = this.getTotalPages();
    if (page < 0) return 0;
    if (page > totalPages - 1) return totalPages - 1;
    return page;
  }

  goFirstPage() {
    this.setState({ currentPage: 0 });
  }

  goPrevPage() {
    this.setState((prev) => ({
      currentPage: this.clampPage(prev.currentPage - 1),
    }));
  }

  goNextPage() {
    this.setState((prev) => ({
      currentPage: this.clampPage(prev.currentPage + 1),
    }));
  }

  goLastPage() {
    this.setState({ currentPage: this.getTotalPages() - 1 });
  }

  regroupReports() {
    /**
     * Group exports by period (1 row per period).
     * Standard + Delayed are grouped in the same row using the format / format_delayed keys.
     */
    let groupedArray = [];

    for (let i = 0; i < this.props.items.length; i++) {
      const item = this.props.items[i];

      const formattedItemPeriod = item.period.replace("-", "/");

      const existingIndex = groupedArray.findIndex(
        (element) => element.period === formattedItemPeriod,
      );

      let format = item.relationships.export_configurations.object.format.toLowerCase();

      const isDelayed = item.object_name.includes("delayed");
      if (isDelayed) format = format + "_delayed";

      if (groupedArray.length === 0 || existingIndex === -1) {
        groupedArray.push({
          period: formattedItemPeriod,
          [format]: item,
        });
      } else {
        groupedArray[existingIndex][format] = item;
      }
    }

    const sortedArray = this.sortPeriodsDesc(groupedArray);

    this.setState((prev) => ({
      sortedData: sortedArray,
      currentPage: this.clampPage(prev.currentPage),
    }));
  }

  sortPeriodsDesc(items) {
    return items.sort((a, b) => {
      const keyA = this.toSortKey(a.period);
      const keyB = this.toSortKey(b.period);
      if (keyA < keyB) return 1;
      if (keyA > keyB) return -1;
      return 0;
    });
  }

  getCols() {
    return [
      {
        name: (
          <span className="export-text-size">
            <FormattedMessage id="accountingExport.billingPeriod" />
          </span>
        ),
        display: (exports) => (
          <div className="text-capitalize">
            <FormattedDate
              value={exports.period.replace("/", "-")}
              year="numeric"
              month="long"
              timeZone="UTC"
            />
          </div>
        ),
        widthPct: 45,
      },
      {
        name: (
          <span className="dt-headers">
            <FormattedMessage id="accountingExport.version" />
          </span>
        ),
        display: (exports) => (
          <div className="flex">
            <div className="version-elements">
              <span className="version-text">
                <FormattedMessage
                  className="version-text"
                  id="accountingExport.acomba"
                />
              </span>
              <DownloadS3Button
                model={ACCOUNTING_EXPORTS}
                blockButton={!exports.acomba}
                identifier={exports.acomba ? exports.acomba.object_name : null}
                attachment={
                  exports.acomba
                    ? {
                        objectPath: exports.acomba.object_path,
                        objectName: exports.acomba.object_name,
                        bucketName: S3_BUCKET.exports.bucket_name,
                        regionName: S3_BUCKET.exports.region_name,
                      }
                    : null
                }
                label=""
              />
            </div>

            <div className="version-elements">
              <span className="version-text">
                <FormattedMessage
                  className="version-text"
                  id="accountingExport.acombaDelayed"
                />
              </span>
              <DownloadS3Button
                model={ACCOUNTING_EXPORTS}
                blockButton={!exports["acomba_delayed"]}
                identifier={
                  exports["acomba_delayed"]
                    ? exports["acomba_delayed"].object_name
                    : null
                }
                attachment={
                  exports["acomba_delayed"]
                    ? {
                        objectPath: exports["acomba_delayed"].object_path,
                        objectName: exports["acomba_delayed"].object_name,
                        bucketName: S3_BUCKET.exports.bucket_name,
                        regionName: S3_BUCKET.exports.region_name,
                      }
                    : null
                }
                label=""
              />
            </div>

            <div className="version-elements">
              <span className="version-text">
                <FormattedMessage
                  className="version-text"
                  id="accountingExport.acomba_csv"
                />
              </span>
              <DownloadS3Button
                model={ACCOUNTING_EXPORTS}
                blockButton={!exports.acomba_csv}
                identifier={
                  exports.acomba_csv ? exports.acomba_csv.object_name : null
                }
                attachment={
                  exports.acomba_csv
                    ? {
                        objectPath: exports.acomba_csv.object_path,
                        objectName: exports.acomba_csv.object_name,
                        bucketName: S3_BUCKET.exports.bucket_name,
                        regionName: S3_BUCKET.exports.region_name,
                      }
                    : null
                }
                label=""
              />
            </div>

            <div className="version-elements">
              <span className="version-text">
                <FormattedMessage
                  className="version-text"
                  id="accountingExport.acomba_csv_delayed"
                />
              </span>
              <DownloadS3Button
                model={ACCOUNTING_EXPORTS}
                blockButton={!exports["acomba_csv_delayed"]}
                identifier={
                  exports["acomba_csv_delayed"]
                    ? exports["acomba_csv_delayed"].object_name
                    : null
                }
                attachment={
                  exports["acomba_csv_delayed"]
                    ? {
                        objectPath: exports["acomba_csv_delayed"].object_path,
                        objectName: exports["acomba_csv_delayed"].object_name,
                        bucketName: S3_BUCKET.exports.bucket_name,
                        regionName: S3_BUCKET.exports.region_name,
                      }
                    : null
                }
                label=""
              />
            </div>
          </div>
        ),
        widthPct: 55,
      },
    ];
  }

  renderPagination() {
    const { currentPage, sortedData } = this.state;

    const totalPages = this.getTotalPages();
    const isFirst = currentPage === 0;
    const isLast = currentPage >= totalPages - 1;

    return (
      <div className="exports-pagination">
        <button
          type="button"
          onClick={this.goFirstPage}
          disabled={isFirst}
          className="exports-pagination__btn"
        >
          {"<<"}
        </button>
        <button
          type="button"
          onClick={this.goPrevPage}
          disabled={isFirst}
          className="exports-pagination__btn exports-pagination__btn--spaced"
        >
          {"<"}
        </button>

        <span className="exports-pagination__page">{currentPage + 1}</span>

        <button
          type="button"
          onClick={this.goNextPage}
          disabled={isLast}
          className="exports-pagination__btn exports-pagination__btn--spaced"
        >
          {">"}
        </button>
        <button
          type="button"
          onClick={this.goLastPage}
          disabled={isLast}
          className="exports-pagination__btn"
        >
          {">>"}
        </button>
      </div>
    );
  }

  render() {
    const pagedData = this.getPagedData();

    return (
      <div>
        <DataTable
          ref="dataTable"
          getData={this.getData}
          cols={this.getCols()}
          data={pagedData}
          offset={0}
          rowsPerPage={ROWS_PER_PAGE}
          rowHeight={60}
          showFilters={false}
          showOverflow={false}
          totalRows={pagedData.length}
        />
        {this.renderPagination()}
      </div>
    );
  }
}

AccountingExportsDataTable.propTypes = {
  items: PropTypes.array,
};

function mapDispatchToProps(dispatch) {
  return {
    dataActions: bindActionCreators(dataActions, dispatch),
  };
}

function mapStateToProps(state) {
  const data = state.data.accountingExports || {};
  return {
    items: data.items || [],
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(AccountingExportsDataTable);



// WEBPACK FOOTER //
// ./src/components/dashboard/importExport/exports/accounting/AccountingExportsDataTable.js